import React, { useEffect, useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, XAxis } from 'recharts';

type Item = {
  ordPro_invoiceDate: string | null;
};

type Props = {
  data: Item[];
  setOldesAges?: any;
};

export const chartColors = [
  '#6366F1', // Indigo (Strong Primary)
  '#2DD4BF', // Teal (Fresh contrast)
  '#8B5CF6', // Violet
  '#FB923C', // Warm Orange (Great for highlights)
  '#38BDF8', // Sky Blue
  '#F472B6', // Pink
  '#A855F7', // Purple
  '#4ADE80', // Emerald Green
  '#FBBF24', // Amber
  '#64748B', // Slate Blue-Gray (Good for "Other" categories)
  '#E879F9', // Fuchsia
  '#22D3EE', // Cyan
  '#F87171', // Soft Red
  '#818CF8', // Soft Indigo
  '#C084FC', // Soft Purple
];

type AgeSegment = {
  ageLabel: string;
  age: number;
  year: number;
  percentage: number;
  color: string;
  count: number;
};

export const InvoiceAgeStackedBar: React.FC<Props> = ({
  data,
  setOldesAges,
}) => {
  const [segments, setSegments] = useState<AgeSegment[]>([]);

  useEffect(() => {
    const now = new Date();
    const ageMap: Record<string, { count: number; year: number; age: number }> =
      {};

    data.forEach((item) => {
      if (item.ordPro_invoiceDate) {
        const invoiceDate = new Date(item.ordPro_invoiceDate);
        let age = now.getFullYear() - invoiceDate.getFullYear();

        const isBeforeAnniversary =
          now.getMonth() < invoiceDate.getMonth() ||
          (now.getMonth() === invoiceDate.getMonth() &&
            now.getDate() < invoiceDate.getDate());
        if (isBeforeAnniversary) age--;

        const year = invoiceDate.getFullYear();
        const key = `Age ${age}`;
        if (!ageMap[key]) {
          ageMap[key] = { count: 0, year, age };
        }
        ageMap[key].count += 1;
      }
    });

    setOldesAges(4);

    const total = Object.values(ageMap).reduce(
      (sum, item) => sum + item.count,
      0
    );

    const sorted = Object.entries(ageMap)
      .sort((a, b) => a[1].age - b[1].age)
      .map(([label, { count, year, age }], index) => ({
        ageLabel: label,
        age,
        year,
        percentage: (count / total) * 100,
        color: chartColors[index % chartColors.length],
        count: count,
      }));

    setSegments(sorted);
  }, [data]);

  const chartData = [
    segments.reduce(
      (acc, segment) => {
        acc[segment.ageLabel] = segment.percentage;
        return acc;
      },
      {} as Record<string, number>
    ),
  ];

  return (
    <div className="flex flex-col items-center justify-center h-[80%]">
      <div className="flex flex-col md:flex-row items-center justify-center gap-6">
        <div className="w-[50px] h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis hide type="category" />
              {segments.reverse().map((segment) => (
                <Bar
                  key={segment.ageLabel}
                  dataKey={segment.ageLabel}
                  stackId="stack"
                  fill={segment.color}
                  barSize={15}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col justify-center max-h-[180px]">
          {[...segments].reverse().map((segment) => (
            <div
              key={segment.ageLabel}
              className="flex items-center text-sm space-x-4 mb-1"
            >
              <div
                className="w-[15px] h-[15px]"
                style={{ backgroundColor: segment.color }}
              />
              <span>
                {segment.age}Y ({segment.count})
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InvoiceAgeStackedBar;
